/**
 * roleLensCopy.js — Lens-Aware Copy Configuration
 * 
 * Per-lens perspective copy for Granullo.
 * This is COPY ONLY — does NOT control permissions or features.
 * 
 * CANONICAL RULES:
 * - Lens changes how Granullo explains itself (narrative framing)
 * - Lens does NOT unlock features or grant access
 * - Lens is PERSPECTIVE ONLY, never behavior
 * - Default lens is 'corporate' if none selected
 * - Lens selection persists per user (localStorage)
 * 
 * LENS (LOCKED v1):
 * - Corporate Lens
 * - Bank Lens
 * - Regulator Lens (RBI)
 * - PE / VC Lens
 * - Insurance Lens
 * - Merchant / Foundation Lens
 */

// =============================================================================
// LENS DEFINITIONS (LOCKED V1 - 6 LENSES)
// =============================================================================

export const ROLE_LENS_OPTIONS = [
  {
    id: 'corporate',
    label: 'Corporate Lens',
    shortLabel: 'Corporate',
    description: 'Program sponsorship and operational governance',
    icon: 'Building2',
    color: 'blue',
    lensPath: '/lens/corporate'
  },
  {
    id: 'bank',
    label: 'Bank Lens',
    shortLabel: 'Bank',
    description: 'Custody, settlement, and compliance execution',
    icon: 'Landmark',
    color: 'teal',
    lensPath: '/lens/bank'
  },
  {
    id: 'regulator',
    label: 'Regulator Lens',
    shortLabel: 'Regulator',
    description: 'Policy intent preservation and system oversight (RBI)',
    icon: 'Shield',
    color: 'amber',
    lensPath: '/lens/regulator'
  },
  {
    id: 'pe-vc',
    label: 'PE / VC Lens',
    shortLabel: 'PE/VC',
    description: 'Portfolio governance and due diligence',
    icon: 'DollarSign',
    color: 'emerald',
    lensPath: '/lens/pe-vc'
  },
  {
    id: 'insurance',
    label: 'Insurance Lens',
    shortLabel: 'Insurance',
    description: 'Risk visibility and claims evidence',
    icon: 'FileCheck',
    color: 'rose',
    lensPath: '/lens/insurance'
  },
  {
    id: 'merchant',
    label: 'Merchant / Foundation Lens',
    shortLabel: 'Merchant',
    description: 'Program participation and fund governance',
    icon: 'Users',
    color: 'violet',
    lensPath: '/lens/merchant'
  }
];

// =============================================================================
// LENS-SPECIFIC COPY (Audit-grade, neutral tone)
// =============================================================================

// Two locked identity lines (verbatim)
export const GRANULLO_IDENTITY = {
  line1: "Granullo is a witness, not a narrator.",
  line2: "It records, it does not decide."
};

// 3C Narrative
export const THREE_C_NARRATIVE = {
  commitments: "What was declared and agreed",
  conditions: "The boundaries within which execution happens", 
  consequences: "What happens when execution diverges from intent"
};

// What Granullo DOES
export const WHAT_GRANULLO_DOES = [
  "Records decisions at the moment they are made",
  "Preserves evidence of execution against intent",
  "Reveals exposure without assigning blame"
];

// What Granullo DOES NOT do
export const WHAT_GRANULLO_DOES_NOT = [
  "Move money or execute transactions",
  "Make decisions or enforce rules",
  "Score, rank, or judge governance quality"
];

export const ROLE_LENS_COPY = {
  corporate: {
    whatYouCare: [
      'Program-level execution evidence',
      'Where spend decisions accumulate ambiguity',
      'Evidence trails for internal audit',
      'Clear boundaries between Corporate and Bank obligations'
    ],
    whatGranulloIs: 'Granullo records evidence of your program execution. It shows where intent was preserved and where gaps appeared.',
    whatGranulloIsNot: 'Granullo does not replace your ERP, optimize processes, or automate decisions. It observes and records.',
    programVsPolicy: 'You sponsor programs that execute policy. Granullo captures evidence at each execution boundary so you can demonstrate compliance.',
    accountability: 'Accountability starts at program creation. Granullo records what you committed to and what evidence exists of execution.',
    boundaries: [
      { role: 'Bank', boundary: 'Bank holds custody and settles transactions. You define the program; Bank executes the rails.' },
      { role: 'Regulator', boundary: 'Regulator defines constraints. Your programs must operate within those constraints.' },
      { role: 'PE/VC', boundary: 'PE/VC evaluates your governance posture. You provide the evidence they need.' }
    ],
    startHere: [
      { label: 'Intent Records', to: '/intent-records', description: 'Your program evidence' },
      { label: 'CBDC Infrastructure', to: '/cbdc', description: 'Understand your role' },
      { label: 'Pricing', to: '/pricing', description: 'Program pricing tiers' }
    ],
    emptyStateStory: 'This surface shows how your programs execute against declared intent. Once you create programs, evidence will appear here.'
  },
  
  bank: {
    whatYouCare: [
      'Custody and settlement evidence',
      'Program-level compliance trails',
      'Clear separation from program sponsorship',
      'Audit-ready disbursement records'
    ],
    whatGranulloIs: 'Granullo records evidence of settlement and custody events. It documents what you executed without replacing your core systems.',
    whatGranulloIsNot: 'Granullo does not enforce rules, block transactions, or replace your banking infrastructure. It records evidence.',
    programVsPolicy: 'You execute settlement within policy constraints. Programs define the rules; you execute the rails. Granullo records the handoff.',
    accountability: 'Accountability starts at the settlement boundary. Granullo records what you received and what you executed.',
    boundaries: [
      { role: 'Corporate', boundary: 'Corporate sponsors programs. You provide custody and settlement. The boundary is clear.' },
      { role: 'Regulator', boundary: 'Regulator defines settlement constraints. You operate within those constraints.' },
      { role: 'Insurance', boundary: 'Insurance evaluates your execution evidence. You provide the audit trail.' }
    ],
    startHere: [
      { label: 'CBDC Infrastructure', to: '/cbdc', description: 'Your role in the system' },
      { label: 'Intent Records', to: '/intent-records', description: 'Program evidence' },
      { label: 'Bank Execution Ledger', to: '/bank-ledger', description: 'Settlement records' }
    ],
    emptyStateStory: 'This surface shows settlement and custody evidence. Once bank operations begin, execution records will appear here.'
  },
  
  regulator: {
    whatYouCare: [
      'Evidence that policy intent was preserved',
      'Visibility into declared gaps',
      'System-wide compliance patterns',
      'Audit trails without operational access'
    ],
    whatGranulloIs: 'Granullo provides evidence that policy intent survived execution. It shows where gaps exist without interfering with operations.',
    whatGranulloIsNot: 'Granullo does not enforce policy, control transactions, or replace regulatory systems. It reveals evidence.',
    programVsPolicy: 'You define policy. Programs execute that policy. Granullo shows you where execution preserved intent and where it diverged.',
    accountability: 'Accountability starts at policy declaration. Granullo records how that policy was interpreted and executed across the system.',
    boundaries: [
      { role: 'Bank', boundary: 'Bank executes settlement. You see evidence of that execution without operational access.' },
      { role: 'Corporate', boundary: 'Corporate sponsors programs. You see evidence of program compliance.' },
      { role: 'Insurance', boundary: 'Insurance assesses risk. You define the constraints they operate within.' }
    ],
    startHere: [
      { label: 'CBDC Infrastructure', to: '/cbdc', description: 'Policy intent preservation' },
      { label: 'Intent Records', to: '/intent-records', description: 'System-wide evidence' },
      { label: 'Preparedness Value', to: '/preparedness', description: 'Aggregate visibility' }
    ],
    emptyStateStory: 'This surface shows system-wide governance evidence. Policy compliance patterns will appear as entities create programs.'
  },
  
  'pe-vc': {
    whatYouCare: [
      'Portfolio-wide governance exposure',
      'Due diligence evidence trails',
      'Governance readiness signals',
      'Economic cost of governance gaps'
    ],
    whatGranulloIs: 'Granullo provides portfolio-level visibility into governance posture. It shows which entities have evidence trails and which have gaps.',
    whatGranulloIsNot: 'Granullo does not score investments, predict outcomes, or replace your diligence process. It reveals evidence.',
    programVsPolicy: 'Your portfolio companies sponsor programs. Granullo shows you whether their execution matches their commitments.',
    accountability: 'Accountability exists at the evidence level. Granullo helps you ask: "Can this company prove what it claims?"',
    boundaries: [
      { role: 'Corporate', boundary: 'Corporate provides evidence. You evaluate that evidence for investment decisions.' },
      { role: 'Insurance', boundary: 'Insurance prices risk based on evidence. You may co-invest with risk-aware capital.' },
      { role: 'Regulator', boundary: 'Regulator defines constraints. Your portfolio companies must comply.' }
    ],
    startHere: [
      { label: 'Preparedness Value', to: '/preparedness', description: 'Portfolio exposure view' },
      { label: 'Intent Records', to: '/intent-records', description: 'Evidence review' },
      { label: 'Pricing', to: '/pricing', description: 'Governance economics' }
    ],
    emptyStateStory: 'This surface shows portfolio governance posture. Evidence from your portfolio companies will appear here.'
  },
  
  insurance: {
    whatYouCare: [
      'Client governance telemetry',
      'Evidence for claims assessment',
      'Risk visibility without operational access',
      'Dispute package generation'
    ],
    whatGranulloIs: 'Granullo provides evidence trails for claims and underwriting. It shows what was declared, what was executed, and where gaps exist.',
    whatGranulloIsNot: 'Granullo does not price risk, approve claims, or replace actuarial models. It provides evidence.',
    programVsPolicy: 'Your clients sponsor programs. Granullo shows you whether their execution created evidence that supports or contradicts claims.',
    accountability: 'Accountability in insurance starts at policy declaration. Granullo ensures evidence exists when claims arise.',
    boundaries: [
      { role: 'Corporate', boundary: 'Corporate is your client. You see their governance evidence for underwriting and claims.' },
      { role: 'Bank', boundary: 'Bank provides settlement evidence. You may need this for claims involving transactions.' },
      { role: 'Regulator', boundary: 'Regulator defines insurance constraints. You operate within those constraints.' }
    ],
    startHere: [
      { label: 'Insurance Console', to: '/insurance', description: 'Client telemetry view' },
      { label: 'Intent Records', to: '/intent-records', description: 'Evidence for claims' },
      { label: 'Preparedness Value', to: '/preparedness', description: 'Risk exposure visibility' }
    ],
    emptyStateStory: 'This surface shows client governance evidence. Telemetry from insured entities will appear here for claims and underwriting.'
  },
  
  merchant: {
    whatYouCare: [
      'Program participation evidence',
      'Fund disbursement records',
      'Compliance with program rules',
      'Foundation grant execution'
    ],
    whatGranulloIs: 'Granullo records evidence of your participation in programs. It shows what you received, what you executed, and what evidence exists.',
    whatGranulloIsNot: 'Granullo does not distribute funds, approve transactions, or manage your operations. It records evidence.',
    programVsPolicy: 'You participate in programs that others sponsor. Granullo records your execution against the program rules.',
    accountability: 'Accountability starts at participation. Granullo records what you committed to and what you delivered.',
    boundaries: [
      { role: 'Corporate', boundary: 'Corporate sponsors the program you participate in. They define the rules; you execute.' },
      { role: 'Bank', boundary: 'Bank settles your transactions. Granullo records the handoff between program and settlement.' },
      { role: 'Regulator', boundary: 'Regulator may audit your participation. Granullo ensures evidence exists.' }
    ],
    startHere: [
      { label: 'Intent Records', to: '/intent-records', description: 'Your participation evidence' },
      { label: 'Start Here Guide', to: '/start-here', description: 'Full system overview' },
      { label: 'How It Works', to: '/how-granullo-works', description: 'Understand the model' }
    ],
    emptyStateStory: 'This surface shows your program participation evidence. Once you join programs, execution records will appear here.'
  }
};

// =============================================================================
// STORAGE & UTILITIES
// =============================================================================

// Default role if none selected
export const DEFAULT_ROLE = 'corporate';

// LocalStorage keys
export const ROLE_LENS_KEY = 'granullo_role_lens';
export const START_HERE_SEEN_KEY = 'granullo_start_here_seen';
export const SHOW_CORPORATE_CONTEXT_KEY = 'granullo_show_corporate_context';

/**
 * Get current role from localStorage
 */
export const getCurrentRole = () => {
  if (typeof window === 'undefined') return DEFAULT_ROLE;
  return localStorage.getItem(ROLE_LENS_KEY) || DEFAULT_ROLE;
};

/**
 * Set role in localStorage
 */
export const setCurrentRole = (role) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(ROLE_LENS_KEY, role);
  // Dispatch custom event so other components can react
  window.dispatchEvent(new CustomEvent('roleLensChanged', { detail: { role } }));
};

/**
 * Check if start page has been seen
 */
export const hasSeenStart = () => {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem(START_HERE_SEEN_KEY) === 'true';
};

/**
 * Mark start page as seen
 */
export const markStartSeen = () => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(START_HERE_SEEN_KEY, 'true');
};

/**
 * Reset first-visit state (for testing)
 */
export const resetFirstVisit = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(START_HERE_SEEN_KEY);
  localStorage.removeItem(ROLE_LENS_KEY);
  localStorage.removeItem(SHOW_CORPORATE_CONTEXT_KEY);
};

/**
 * Get Corporate context toggle state
 */
export const getShowCorporateContext = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SHOW_CORPORATE_CONTEXT_KEY) === 'true';
};

/**
 * Set Corporate context toggle state
 */
export const setShowCorporateContext = (show) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SHOW_CORPORATE_CONTEXT_KEY, show ? 'true' : 'false');
  window.dispatchEvent(new CustomEvent('corporateContextChanged', { detail: { show } }));
};

// =============================================================================
// SESSION-BASED RE-OFFER LOGIC (V1.1)
// =============================================================================

// SessionStorage key for role re-offer dismissal (resets each browser session)
export const ROLE_REOFFER_DISMISSED_KEY = 'granullo_role_reoffer_dismissed';

/**
 * Check if role re-offer should be shown
 * Shows on each new browser session for logged-out users
 */
export const shouldShowRoleReoffer = () => {
  if (typeof window === 'undefined') return false;
  
  // Don't show if user hasn't set a role yet (they need the "Choose perspective" CTA instead)
  const hasRole = localStorage.getItem(ROLE_LENS_KEY);
  if (!hasRole) return false;
  
  // Don't show if already dismissed this session
  const dismissed = sessionStorage.getItem(ROLE_REOFFER_DISMISSED_KEY);
  if (dismissed === 'true') return false;
  
  return true;
};

/**
 * Dismiss role re-offer for this session
 */
export const dismissRoleReoffer = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(ROLE_REOFFER_DISMISSED_KEY, 'true');
};

/**
 * Check if user has explicitly selected a role (not just using default)
 */
export const hasExplicitRole = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(ROLE_LENS_KEY) !== null;
};

/**
 * Reset all role-related state (full reset)
 * Clears: role, corporate context, start seen, re-offer dismissal
 */
export const resetAllRoleState = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ROLE_LENS_KEY);
  localStorage.removeItem(SHOW_CORPORATE_CONTEXT_KEY);
  localStorage.removeItem(START_HERE_SEEN_KEY);
  sessionStorage.removeItem(ROLE_REOFFER_DISMISSED_KEY);
  // Dispatch events to notify other components
  window.dispatchEvent(new CustomEvent('roleLensChanged', { detail: { role: null } }));
  window.dispatchEvent(new CustomEvent('roleReset', {}));
};

/**
 * Get role copy for a specific role
 */
export const getRoleCopy = (roleId) => {
  return ROLE_LENS_COPY[roleId] || ROLE_LENS_COPY[DEFAULT_ROLE];
};

/**
 * Get role option by ID
 */
export const getRoleOption = (roleId) => {
  return ROLE_LENS_OPTIONS.find(r => r.id === roleId) || ROLE_LENS_OPTIONS.find(r => r.id === DEFAULT_ROLE);
};

/**
 * Get Corporate role copy (for hybrid context)
 */
export const getCorporateCopy = () => {
  return ROLE_LENS_COPY.corporate;
};
